import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle,
  Send,
  CheckCircle
} from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    setIsSubmitted(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      city: '',
      serviceType: '',
      message: ''
    });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const contactInfo = [
    {
      icon: <Phone className="h-8 w-8 text-yellow-500" />,
      title: "Phone",
      details: ["+91 7893987771", "+91 8096481881"],
      action: "tel:+917893987771"
    },
    {
      icon: <Mail className="h-8 w-8 text-yellow-500" />,
      title: "Email",
      details: ["steelinvisiblegrillsints@gmail.com"],
      action: "mailto:steelinvisiblegrillsints@gmail.com"
    },
    {
      icon: <MapPin className="h-8 w-8 text-yellow-500" />,
      title: "Service Areas",
      details: ["Hyderabad"],
      action: "#"
    },
    {
      icon: <Clock className="h-8 w-8 text-yellow-500" />,
      title: "Working Hours",
      details: ["Mon-Fri: 9:00 AM - 6:00 PM, Sat: 9:00 AM - 4:00 PM", "Sun: closed"],
      action: "#"
    }
  ];

  const cities = [
    'Hyderabad & Secunderabad', 'Banjara Hills & Jubilee Hills', 'Gachibowli & HITEC City', 
    'Kondapur & Madhapur', 'Begumpet & Somajiguda', 'Ameerpet & Kukatpally',
    'Miyapur & Bachupally', 'Nizampet & Chandanagar', 'Serilingampally & Rajendra Nagar',
    'Attapur & Manikonda', 'Financial District & Nanakramguda', 'Kokapet & Tellapur',
    'Narsingi & Old City', 'Charminar & Mehdipatnam', 'Tolichowki & Malakpet',
    'Dilsukhnagar & L.B. Nagar', 'Uppal & Nagole', 'Boduppal & Alwal',
    'Malkajgiri & Kapra', 'ECIL & Tarnaka', 'Osmania University & Nallakunta',
    'Musheerabad & Kachiguda', 'Abids & Koti', 'Sultan Bazar & Nampally',
    'Basheerbagh & Khairatabad', 'Panjagutta & SR Nagar', 'Erragadda & Sanathnagar',
    'Balanagar & Patancheru', 'BHEL & IDA', 'Jeedimetla & Qutubullapur',
    'Medchal & Shamirpet', 'Gandipet & Shamshabad', 'Rajiv Gandhi International Airport',
    'Cyberabad & Raidurg', 'Gachibowli Stadium'
  ];

  const serviceTypes = [
    'Invisible Grill for Balconies',
    'Invisible Grill for Windows',
    'Invisible Grill for Apartments',
    'Pull & Dry Cloth Hangers',
    'Ceiling Cloth Hangers',
    'Pull & Dry Cloth Hangers for Balconies',
    'Invisible Grill Dealers',
    'Invisible Grill Fixing Charges',
    'Invisible Grill for Balcony Near Me',
    'Invisible Grill for Balcony Price',
    'Invisible Grill for Child Safety',
    'Invisible Grill Manufacturer',
    'Stainless Steel Invisible Grill',
    'Pull & Dry Cloth Hangers Fixing',
    'Pull & Dry Cloth Hangers Installation'
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('/images/safety-nets/slider-7-3.jpg')`
        }}></div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-blue-900/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl lg:text-5xl font-bold mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white max-w-3xl mx-auto font-medium"
          >
            Get in touch with our invisible grill experts for a free consultation and quote. 
            We're here to help you secure your home with style!
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <div className="mb-4 flex justify-center">{info.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                <div className="space-y-2">
                  {info.details.map((detail, idx) => (
                    <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                  ))}
                </div>
                {info.action !== "#" && (
                  <a
                    href={info.action}
                    className="inline-block mt-4 text-yellow-600 hover:text-yellow-700 font-medium"
                  >
                    Contact Now
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Get Your Free Invisible Grill Quote
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and our invisible grill experts will get back to you within 24 hours 
                with a customized quote and consultation.
              </p>

              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Thank you! We'll contact you soon with your invisible grill quote.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <select
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    >
                      <option value="">Select your city</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-2">
                    Service Type
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Select service type</option>
                    {serviceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Requirements
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Tell us about your specific requirements..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 py-4 px-6 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Get Free Quote
                </button>
              </form>
            </motion.div>

            {/* Map & Quick Contact */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-gray-100 p-8 rounded-lg h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Quick Contact
                </h3>
                
                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <MessageCircle className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">WhatsApp</h4>
                      <p className="text-gray-600">Get instant response on WhatsApp</p>
                      <a
                        href="https://wa.me/917893987771"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-yellow-600 hover:text-yellow-700 font-medium"
                      >
                        Chat Now
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Phone className="h-6 w-6 text-yellow-500 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Call Us</h4>
                      <p className="text-gray-600">Speak directly with our experts</p>
                      <a
                        href="tel:+917893987771"
                        className="text-yellow-600 hover:text-yellow-700 font-medium"
                      >
                        +91 7893987771
                      </a>
                    </div>
                  </div>
                </div>

                {/* Google Maps Section */}
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPin className="h-5 w-5 text-yellow-500 mr-2" />
                    Find Us on Google Maps
                  </h4>
                  <div className="mb-4">
                    <p className="text-gray-600 text-sm mb-2">
                      Green Park Colony, Road No.9
                    </p>
                    <p className="text-gray-600 text-sm">
                      Karmanghat, Hyderabad, Telangana, India
                    </p>
                  </div>
                  <div className="relative w-full h-64 rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.123456789!2d79.0994768!3d13.2202461!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDEzJzEyLjAiTiA3OcKwMDUnNTguMSJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Steel Invisible Grills Location"
                    ></iframe>
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <a
                      href="https://maps.google.com/maps?q=13.2202461,79.0994768&z=17&hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-medium"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Open in Google Maps
                    </a>
                    <a
                      href="https://maps.google.com/maps/dir/?api=1&destination=13.2202461,79.0994768"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                      Get Directions
                    </a>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Why Choose Us?</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Free site survey and consultation</li>
                    <li>• Customized safety solutions</li>
                    <li>• Quality assured materials</li>
                    <li>• 5+ year warranty on installations</li>
                    <li>• Professional installation team</li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas Map */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Service Areas Across India
            </h2>
            <p className="text-xl text-gray-300">
              We provide safety nets installation services across these major cities
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
            {cities.map((city, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-800 p-4 rounded-lg text-center hover:bg-yellow-600 transition-colors cursor-pointer"
              >
                <MapPin className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                <p className="font-medium">{city}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Ensure Safety?
          </h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Don't wait! Contact us today for a free consultation and start protecting your premises with our safety solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+917893987771"
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
                              Call Now: +91 7893987771
            </a>
            <a
              href="https://wa.me/917893987771"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
